import ArrayUtils from '../utils/ArrayUtils';
import CoreUtils from '../utils/CoreUtils';
import StringUtils from '../utils/StringUtils';
import TimeUtils from '../utils/TimeUtils';

import DataItem from './DataItem';
import DataObjectList from './DataObjectList';

import Comparison from './Comparison';
import DefaultValue from './DefaultValue';
import Serialization from './Serialization';
import Validation from './Validation';

export default class DataObject extends DataItem {
  constructor(metadataRules) {
    super(metadataRules);

    this.childDataItemStateListener = this.childDataItemStateUpdated.bind(this);
    this.childDataItems = [];

    this.propertyNames = [];
    this.projectedPropertyNames = [];
    this.mappedProjectedPropertyValues = {};
    this.childClassTypes = {};
    this.originalValues = {};

    this.modifiedPropertyNames = [];

    this.isDeleted = false;
    this.updateErrorMessage = null;

    this.dynamicMetadataRules = {};
    this.usingDynamicSchema = this.useDynamicSchema();
    this.usingInheritedMetadataRules = this.useInheritedMetadataRules();

    this.defaultOverrides = this.getDefaultOverrides();
  }

  initDataObject(dtoObject) {
    if (CoreUtils.isUndefinedOrNull(dtoObject))
      return;

    this.dispose();
    this.isNew = false;
    this.processPropertyValues(dtoObject);
  }

  createDataObject() {
    this.dispose();
    this.isNew = true;
    this.processPropertyValues(null);
  }

  setIsDeleted(isDeleted) {
    if (CoreUtils.isUndefinedOrNull(isDeleted) || typeof isDeleted !== 'boolean')
      isDeleted = true;

    if (isDeleted === this.isDeleted)
      return;

    this.isDeleted = isDeleted;

    var state = {
      isDeleted: isDeleted
    };

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  setUpdateErrorMessage(updateErrorMessage) {
    if (updateErrorMessage === this.updateErrorMessage)
      return;

    if (CoreUtils.isDefinedAndNotNull(updateErrorMessage) && typeof updateErrorMessage !== 'string')
      return;

    this.updateErrorMessage = updateErrorMessage;
    var state = {
      updateErrorMessage: this.updateErrorMessage
    }

    this.notifyStateUpdated(state);
  }

  dispose() {
    this.childDataItems.forEach(dataObject => {
      dataObject.removeStateListener(this.childDataItemStateListener);
      dataObject.removeProjectedValues(this.projectedValues);
      dataObject.dispose();
    });

    this.childDataItems = [];

    super.dispose();
  }

  getPropertyNames() {
    return [];
  }

  getChildClassTypes() {
    return {};
  }

  getDefaultOverrides() {
    return {};
  }

  useDynamicSchema() {
    return false;
  }

  useInheritedMetadataRules() {
    return true;
  }

  getProjectedPropertyNames() {
    return [];
  }

  getMappedProjectedPropertyValues() {
    return {};
  }

  convertFromDtoValue(propertyName, value, metadataRule) {
    return Serialization.deserializeValue(value, metadataRule);
  }

  convertToDtoValue(propertyName, value, metadataRule) {
    return Serialization.serializeValue(value, metadataRule);
  }

  resolveMetadataRule(propertyName) {
    return this.usingDynamicSchema && !this.usingInheritedMetadataRules
      ? this.dynamicMetadataRules[propertyName]
      : this.metadataRules[propertyName];
  }

  resolvePropertyNames(dtoObject) {
    var propertyNames = this.getPropertyNames();
    if (CoreUtils.isUndefinedOrNull(propertyNames) || !Array.isArray(propertyNames))
      propertyNames = [];

    if (propertyNames.length === 0 && CoreUtils.isDefinedAndNotNull(dtoObject))
      propertyNames = CoreUtils.getObjectPropertyNames(dtoObject);

    return propertyNames;
  }

  resolveMappedProjectedPropertyValues() {
    var mappedProjectedPropertyValues = {};

    var mappedValues = this.getMappedProjectedPropertyValues();
    if (CoreUtils.isDefinedAndNotNull(mappedValues)) {
      CoreUtils.iterateProperties(mappedValues, (propertyName, config) => {
        var projectedPropertyName = this.getRemoteProjectedPropertyName(
          config.classType, config.propertyName
        );

        mappedProjectedPropertyValues[projectedPropertyName] = {
          localPropertyName: propertyName,
          mode: config.mode,
          nestedPath: config.nestedPath
        };
      });
    }

    return mappedProjectedPropertyValues;
  }

  processPropertyValues(dtoObject) {
    this.releaseChildDataItems();

    this.propertyNames = this.resolvePropertyNames(dtoObject);
    this.projectedPropertyNames = this.getProjectedPropertyNames();
    this.mappedProjectedPropertyValues = this.resolveMappedProjectedPropertyValues();
    this.childClassTypes = this.getChildClassTypes();
    this.originalValues = {};

    this.propertyNames.forEach(propertyName =>
      this.processPropertyValue(dtoObject, propertyName)
    );

    this.processItemIsModified();
    this.processItemHasErrors();

    if (CoreUtils.getPropertyCount(this.projectedValues) > 0)
      this.propogateProjectedValues();
  }

  processPropertyValue(dtoObject, propertyName) {
    var metadataRule = this.resolveMetadataRule(propertyName);

    var childClassType = this.childClassTypes[propertyName];
    if (CoreUtils.isDefinedAndNotNull(childClassType) && !this.usingDynamicSchema) {

      var value = CoreUtils.isDefinedAndNotNull(dtoObject)
        ? dtoObject[propertyName]
        : undefined;

      var childObject = new childClassType(this.metadataRules);

      if (childObject instanceof DataObject)
        childObject.initDataObject(value);
      else if (childObject instanceof DataObjectList)
        childObject.initDataObjects(value);

      childObject.updateProjectedValues(this.projectedValues);
      childObject.addStateListener(this.childDataItemStateListener);
      this.childDataItems.push(childObject);

      this[propertyName] = childObject;
      return;
    }

    this[propertyName] = CoreUtils.isDefinedAndNotNull(dtoObject)
      ? this.convertFromDtoValue(propertyName, dtoObject[propertyName], metadataRule)
      : this.resolveDefaultValue(propertyName, metadataRule);

    this.originalValues[propertyName] = this[propertyName];

    if (this.usingDynamicSchema) {
      var metadataRulePropertyName = DataObject.getMetadataRulePropertyName(propertyName);
      this[metadataRulePropertyName] = metadataRule;

      var displayNamePropertyName = DataObject.getDisplayNamePropertyName(propertyName);
      this[displayNamePropertyName] = CoreUtils.isDefinedAndNotNull(metadataRule)
        ? metadataRule.displayName
        : '';
    } else if (CoreUtils.isDefinedAndNotNull(metadataRule) && metadataRule.type === 'select') {
      var optionsPropertyName = DataObject.getOptionsPropertyName(propertyName);
      this[optionsPropertyName] = metadataRule.options;
    }

    var modifiedPropertyName = DataObject.getModifiedPropertyName(propertyName);
    this[modifiedPropertyName] = false;

    var errorPropertyName = DataObject.getErrorPropertyName(propertyName);
    this[errorPropertyName] = this.validatePropertyValue(propertyName, this[propertyName]);

    if (this.projectedPropertyNames.indexOf(propertyName) !== -1)
      this.projectedValues[this.getLocalProjectedPropertyName(propertyName)] = this[propertyName];
  }

  matchSchemaValues(metadataRules) {
    if (!this.usingDynamicSchema)
      return;

    this.dynamicMetadataRules = CoreUtils.isDefinedAndNotNull(metadataRules)
      ? metadataRules
      : {};

    var oldPropertyNames = this.propertyNames;
    this.propertyNames = this.resolvePropertyNames(this.dynamicMetadataRules);

    var state = {};

    if (!ArrayUtils.areArraysEquivalent(this.propertyNames, oldPropertyNames))
      state.propertyNames = this.propertyNames;

    this.propertyNames.forEach(propertyName => {
      var metadataRule = this.resolveMetadataRule(propertyName);

      var metadataRulePropertyName = DataObject.getMetadataRulePropertyName(propertyName);
      var existingMetadataRule = this[metadataRulePropertyName];

      var value = this[propertyName];
      var originalValue = this.originalValues[propertyName];

      if (CoreUtils.isDefinedAndNotNull(metadataRule)) {
        if (CoreUtils.isUndefinedOrNull(existingMetadataRule)) {
          value = this.convertFromDtoValue(propertyName, value, metadataRule);
          originalValue = this.convertFromDtoValue(propertyName, originalValue, metadataRule);
        }

        if (oldPropertyNames.indexOf(propertyName) === -1) {
          value = this.resolveDefaultValue(propertyName, metadataRule);
          originalValue = value;
        }

      } else if (CoreUtils.isDefinedAndNotNull(existingMetadataRule)) {
        value = this.convertToDtoValue(propertyName, value, existingMetadataRule);
        originalValue = this.convertToDtoValue(propertyName, originalValue, existingMetadataRule);
      }

      this[propertyName] = value;
      state[propertyName] = this[propertyName];

      this.originalValues[propertyName] = originalValue;

      this[metadataRulePropertyName] = metadataRule;
      state[metadataRulePropertyName] = metadataRule;

      var displayNamePropertyName = DataObject.getDisplayNamePropertyName(propertyName);
      state[displayNamePropertyName] = CoreUtils.isDefinedAndNotNull(metadataRule)
        ? metadataRule.displayName
        : propertyName;

      this.processPropertyIsModified(propertyName, this[propertyName], state);
      this.processPropertyError(propertyName, this[propertyName], state);
    });

    oldPropertyNames.forEach(propertyName => {
      if (this.propertyNames.indexOf(propertyName) !== -1)
        return;

      var displayNamePropertyName = DataObject.getMetadataRulePropertyName(propertyName);
      var metadataRulePropertyName = DataObject.getMetadataRulePropertyName(propertyName);
      var modifiedPropertyName = DataObject.getModifiedPropertyName(propertyName);
      var errorPropertyName = DataObject.getErrorPropertyName(propertyName);

      state[propertyName] = undefined;
      delete this[propertyName];

      delete this.originalValues[propertyName];

      state[displayNamePropertyName] = undefined;

      state[metadataRulePropertyName] = undefined;
      delete this[metadataRulePropertyName];

      state[modifiedPropertyName] = undefined;
      delete this[modifiedPropertyName];

      state[errorPropertyName] = undefined;
      delete this[errorPropertyName];
    });

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);

    if (CoreUtils.getPropertyCount(this.projectedValues) > 0)
      this.propogateProjectedValues();
  }

  resolveDefaultValue(propertyName, metadataRule) {
    var defaultOverride = this.defaultOverrides[propertyName];
    if (CoreUtils.isUndefinedOrNull(defaultOverride))
      return DefaultValue.resolveValue(metadataRule);

    return typeof defaultOverride === 'function'
      ? defaultOverride(propertyName)
      : defaultOverride;
  }

  getDtoObject() {
    var dtoObject = {};

    this.propertyNames.forEach(
      propertyName => {
        if (this.isChildDataItem(propertyName)) {
          var value = this[propertyName];

          if (CoreUtils.isUndefinedOrNull(value))
            return null;

          dtoObject[propertyName] = value instanceof DataObjectList
            ? value.getDtoObjects()
            : value.getDtoObject();
        } else {
          var metadataRule = this.resolveMetadataRule(propertyName);
          dtoObject[propertyName] = this.convertToDtoValue(propertyName, this[propertyName], metadataRule);
        }
      }
    );

    return dtoObject;
  }

  getOriginalValue(propertyName) {
    return this.originalValues[propertyName];
  }

  updateProjectedValues(projectedValues) {
    super.updateProjectedValues(projectedValues);
    this.updateLocalProjectedValues(projectedValues);
  }

  updateLocalProjectedValues(projectedValues) {
    CoreUtils.iterateProperties(projectedValues, (projectedPropertyName, projectedValue) => {
      var config = this.mappedProjectedPropertyValues[projectedPropertyName];
      if (CoreUtils.isDefinedAndNotNull(config)) {
        var value = projectedValue;
        if (CoreUtils.isDefinedAndNotNull(config.nestedPath) && Array.isArray(config.nestedPath)) {
          config.nestedPath.forEach(path => {
            if (CoreUtils.isDefinedAndNotNull(value))
              value = value[path];
          });
        }

        if (config.mode === 'overwrite')
          this.updateProperty(config.localPropertyName, value);
        else if (config.mode === 'match-schema') {
          var dataObject = this[config.localPropertyName];
          if (CoreUtils.isDefinedAndNotNull(dataObject) && dataObject instanceof DataObject)
            dataObject.matchSchemaValues(value);
        }
      }
    });
  }

  removeProjectedValues(projectedValues) {
    this.childDataItems.forEach(childDataItem =>
      childDataItem.removeProjectedValues(projectedValues)
    );

    super.removeProjectedValues(projectedValues);
  }

  propogateProjectedValues() {
    super.propogateProjectedValues();

    this.updateLocalProjectedValues(this.projectedValues);

    this.childDataItems.forEach(childDataItem =>
      childDataItem.updateProjectedValues(this.projectedValues)
    );
  }

  revertItemValues() {
    if (!this.isModified)
      return;

    var state = {};

    this.propertyNames.forEach(propertyName => {
      if (this.isChildDataItem(propertyName)) {
        this[propertyName].revertItemValues();
      } else {
        var modifiedPropertyName = DataObject.getModifiedPropertyName(propertyName);
        if (!this[modifiedPropertyName])
          return;

        this[propertyName] = this.originalValues[propertyName];
        state[propertyName] = this[propertyName];

        this.processPropertyIsModified(propertyName, this[propertyName], state);
        this.processPropertyError(propertyName, this[propertyName], state);

        if (this.projectedPropertyNames.indexOf(propertyName) !== -1)
          this.projectedValues[this.getLocalProjectedPropertyName(propertyName)] = this[propertyName];
      }
    });

    if (this.isDeleted) {
      this.isDeleted = false;
      state.isDeleted = false;
    }

    if (this.updateErrorMessage !== null) {
      this.updateErrorMessage = null;
      state.updateErrorMessage = null;
    }

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);

    if (CoreUtils.getPropertyCount(this.projectedValues) > 0)
      this.propogateProjectedValues();
  }

  commitUpdatedValues(dtoObject) {
    if (!this.isModified)
      return;

    var state = {};

    this.originalValues = {};

    this.propertyNames.forEach(propertyName => {
      if (dtoObject[propertyName] === undefined)
        return;

      var metadataRule = this.resolveMetadataRule(propertyName);

      if (this.isChildDataItem(propertyName)) {
        var dtoChildObject = CoreUtils.isDefinedAndNotNull(dtoObject)
          ? this.convertFromDtoValue(propertyName, dtoObject[propertyName], metadataRule)
          : undefined;

        this[propertyName].commitUpdatedValues(dtoChildObject);
      } else {
        var value = CoreUtils.isDefinedAndNotNull(dtoObject)
          ? this.convertFromDtoValue(propertyName, dtoObject[propertyName], metadataRule)
          : this[propertyName];

        this[propertyName] = value;
        state[propertyName] = value;

        this.originalValues[propertyName] = value;

        this.processPropertyIsModified(propertyName, value, state);
        this.processPropertyError(propertyName, value, state);
      }
    });

    if (this.isNew) {
      this.isNew = false;
      state.isNew = false;
    }

    if (this.updateErrorMessage !== null) {
      this.updateErrorMessage = null;
      state.updateErrorMessage = null;
    }

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  getState() {
    var state = super.getState();

    state.isNew = this.isNew;
    state.isDeleted = this.isDeleted;
    state.updateErrorMessage = this.updateErrorMessage;
    state.propertyNames = this.propertyNames;

    this.propertyNames.forEach(
      propertyName => {

        state[propertyName] = this[propertyName];

        if (this.usingDynamicSchema) {
          var metadataRule = this.resolveMetadataRule(propertyName);

          var metadataRulePropertyName = DataObject.getMetadataRulePropertyName(propertyName);
          state[metadataRulePropertyName] = metadataRule;

          var displayNamePropertyName = DataObject.getDisplayNamePropertyName(propertyName);
          state[displayNamePropertyName] = CoreUtils.isDefinedAndNotNull(metadataRule)
            ? metadataRule.displayName
            : '';
        } else {
          var optionsPropertyName = DataObject.getOptionsPropertyName(propertyName);
          if (CoreUtils.isDefinedAndNotNull(this[optionsPropertyName]))
            state[optionsPropertyName] = this[optionsPropertyName];
        }

        var modifiedPropertyName = DataObject.getModifiedPropertyName(propertyName);
        state[modifiedPropertyName] = this[modifiedPropertyName];

        var errorPropertyName = DataObject.getErrorPropertyName(propertyName);
        state[errorPropertyName] = this[errorPropertyName];
      }
    );

    return state;
  }

  getModifiedPropertyNames() {
    return Array.from(this.modifiedPropertyNames);
  }

  isChildDataItem(propertyName) {
    var value = this[propertyName];

    return !CoreUtils.isUndefinedOrNull(value)
      && value instanceof DataItem;
  }

  releaseChildDataItems() {
    this.childDataItems.forEach(childDataItem =>
      childDataItem.removeStateListener(this.childDataItemStateListener)
    );
    this.childDataItems = [];
  }

  childDataItemStateUpdated() {
    var state = {};

    var updateIsModified = this.processItemIsModified(state);
    var updateHasErrors = this.processItemHasErrors(state);

    if (!updateIsModified && !updateHasErrors)
      return;

    this.notifyStateUpdated(state);
  }

  validatePropertyValue(propertyName, value) {
    var metadataRule = this.resolveMetadataRule(propertyName);

    return CoreUtils.isDefinedAndNotNull(metadataRule)
      ? Validation.validateValue(value, metadataRule)
      : null;
  }

  updateProperty(propertyName, value) {
    if (this.propertyNames.indexOf(propertyName) === -1
      || this.isChildDataItem(propertyName))
      return;

    var metadataRule = this.resolveMetadataRule(propertyName);
    if (CoreUtils.isDefinedAndNotNull(metadataRule)) {
      if (metadataRule.type === 'time')
        value = TimeUtils.filterTime(value, true);
    }

    var oldValue = this[propertyName];
    if (Comparison.areValuesEqual(value, oldValue, metadataRule))
      return;

    this[propertyName] = value;

    var state = {
      [propertyName]: value
    };

    if (this.updateErrorMessage !== null) {
      this.updateErrorMessage = null;
      state.updateErrorMessage = null;
    }

    this.processPropertyIsModified(propertyName, value, state);
    this.processItemIsModified(state);
    this.processPropertyError(propertyName, value, state);
    this.processItemHasErrors(state);

    this.notifyStateUpdated(state);

    if (this.projectedPropertyNames.indexOf(propertyName) !== -1) {
      this.projectedValues[this.getLocalProjectedPropertyName(propertyName)] = value;
      this.propogateProjectedValues();
    }
  }

  processPropertyIsModified(propertyName, value, state) {
    var metadataRule = this.resolveMetadataRule(propertyName);

    var originalValue = this.originalValues[propertyName];

    var modifiedPropertyName = DataObject.getModifiedPropertyName(propertyName);
    var isPropertyModified = !Comparison.areValuesEqual(value, originalValue, metadataRule);

    if (this[modifiedPropertyName] === isPropertyModified)
      return;

    this[modifiedPropertyName] = isPropertyModified;
    state[modifiedPropertyName] = isPropertyModified;
  }

  processPropertyError(propertyName, value, state) {
    var errorPropertyName = DataObject.getErrorPropertyName(propertyName);
    var propertyError = this.validatePropertyValue(propertyName, value);

    if (this[errorPropertyName] === propertyError)
      return;

    this[errorPropertyName] = propertyError
    state[errorPropertyName] = propertyError;
  }

  resolveItemIsModified() {
    this.modifiedPropertyNames.length = 0;

    this.propertyNames.forEach(propertyName => {
      if (this.resolvePropertyIsModified(propertyName))
        this.modifiedPropertyNames.push(propertyName);
    });

    return this.isDeleted || this.modifiedPropertyNames.length > 0;
  }

  resolveItemHasErrors() {
    return !this.isDeleted && this.propertyNames
      .some(propertyName => this.resolvePropertyHasError(propertyName));
  }

  resolvePropertyIsModified(propertyName) {
    if (!this.isChildDataItem(propertyName))
      return this[DataObject.getModifiedPropertyName(propertyName)];

    var value = this[propertyName];
    if (CoreUtils.isUndefinedOrNull(value))
      return false;

    return value.isModified;
  }

  resolvePropertyHasError(propertyName) {
    if (!this.isChildDataItem(propertyName))
      return StringUtils.isNonEmptyString(
        this[DataObject.getErrorPropertyName(propertyName)]
      );

    var value = this[propertyName];
    if (CoreUtils.isUndefinedOrNull(value))
      return false;

    return value.hasErrors;
  }

  getLocalProjectedPropertyName(propertyName) {
    return `${Object.getPrototypeOf(this).constructor.name}_${propertyName}`;
  }

  getRemoteProjectedPropertyName(classType, propertyName) {
    return `${classType.name}_${propertyName}`;
  }

  static getModifiedPropertyName(propertyName) {
    return `${propertyName}_isModified`;
  }

  static getErrorPropertyName(propertyName) {
    return `${propertyName}_error`;
  }

  static getOptionsPropertyName(propertyName) {
    return `${propertyName}_options`;
  }

  static getDisplayNamePropertyName(propertyName) {
    return `${propertyName}_displayName`;
  }

  static getMetadataRulePropertyName(propertyName) {
    return `${propertyName}_metadataRule`;
  }
}